import type { AbSegment, PageType } from '@/types/nav-types'

import { AB_EXPERIMENTS, type AbExperiment } from './experiments'

export type ExperimentAssignments = Record<string, AbSegment>

// Keys cannot contain separators, and end in a run number by convention.
// The final character of each token is the variant: bhaqv1b = bhaqv1, arm b.
export const encodeExperimentAssignments = (assignments: ExperimentAssignments): string =>
  Object.entries(assignments)
    .filter(([key, variant]) => /^[a-z][a-z0-9]{0,5}$/.test(key) && (variant === 'a' || variant === 'b'))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, variant]) => `${key}${variant}`)
    .join('-')

// The same qualified label is stored in affiliate_sessions.test_variant.
export const formatExperimentAssignments = (assignments: ExperimentAssignments): string =>
  encodeExperimentAssignments(assignments)
    .split('-')
    .filter(Boolean)
    .map((token) => `${token.slice(0, -1)}:${token.slice(-1)}`)
    .join(',')

export const experimentMatchesPage = (experiment: AbExperiment, pathname: string, pageType?: PageType): boolean =>
  experiment.scopes.some((scope) => {
    if (scope.match === 'exact') {
      return pathname === scope.path
    }
    return pathname.startsWith(scope.path) && (scope.match !== 'page-type' || scope.pageType === pageType)
  })

// No clock or browser APIs: safe for server-rendered, cached affiliate links.
// Client callers supply a timestamp to exclude pre-launch and ended exposures.
export const pageExperimentAssignments = ({
  pathname,
  pageType,
  segment,
  at,
}: {
  pathname: string
  pageType?: PageType
  segment?: string
  at?: number
}): ExperimentAssignments => {
  if (segment !== 'a' && segment !== 'b') {
    return {}
  }
  return Object.fromEntries(
    AB_EXPERIMENTS.filter(
      (experiment: AbExperiment) =>
        experiment.status === 'running' &&
        experimentMatchesPage(experiment, pathname, pageType) &&
        (at === undefined ||
          (at >= Date.parse(experiment.startedAt) && (!experiment.endedAt || at < Date.parse(experiment.endedAt))))
    ).map((experiment) => [experiment.trackingKey, segment])
  )
}
